import React, { useEffect, useState } from 'react';
import { categoryService, clientService, membershipService } from '../services/api';
import { maskCPF, maskPhone, isValidEmail, isValidCPF, maskDate, isValidDate, isoToDate, dateToIso } from '../utils/masks';

function formatMinutes(value) {
  const total = Math.max(0, Math.floor(Number(value) || 0));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours && minutes) return `${hours}h ${String(minutes).padStart(2, '0')}min`;
  if (hours) return `${hours}h`;
  return `${minutes}min`;
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', nickname: '', cpf: '', phone: '', email: '', birth_date: '', notes: '', allow_multi_login: false, is_restricted: false, restriction_reason: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [plans,setPlans]=useState([]);
  const [valueCategories,setValueCategories]=useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });

  const load = async (s = search, page = pagination.page) => {
    const res = await clientService.getAll({ search: s, page, page_size: pagination.page_size, paginated: 1 });
    setClients(res.data.data || []);
    setPagination(res.data.pagination || { page, page_size: pagination.page_size, total: 0, pages: 1 });
  };

  useEffect(() => {
    load();
    membershipService.getAll().then(r=>setPlans(r.data.filter(p=>p.is_active)));
    categoryService.getAll('device').then(r=>setValueCategories(r.data));
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    load(e.target.value, 1);
  };

  const handleSave = async () => {
    if (!form.name || !form.name.trim()) {
      alert('Informe o nome do cliente');
      return;
    }
    const cleanDate = (form.birth_date || '').replace(/\D/g, '');
    if (cleanDate.length !== 8 || !isValidDate(form.birth_date)) {
      alert('A data de aniversário deve estar no formato DD/MM/AAAA (ex: 15/08/1995)');
      return;
    }
    if (form.password && form.password.trim() && form.password.trim().length < 4) {
      alert('A senha deve ter ao menos 4 caracteres');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        birth_date: dateToIso(form.birth_date)
      };
      let clientId;
      if (modal.client) {
        await clientService.update(modal.client.id, payload);
        clientId = modal.client.id;
      } else {
        const res = await clientService.create(payload);
        clientId = res.data.id;
      }
      // se preencheu uma senha, define/atualiza a senha de login do cliente
      if (form.password && form.password.trim()) {
        await clientService.setPassword(clientId, { password: form.password.trim() });
      }
      await membershipService.assign({client_id:clientId,plan_id:form.membership_plan_id||null});
      setModal(null);
      load(search);
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setShowPassword(false);
    setForm({ name: '', nickname: '', cpf: '', phone: '', email: '', birth_date: '', notes: '', password: '', allow_multi_login: false, is_restricted: false, restriction_reason: '', membership_plan_id:'' });
    setModal({ type: 'form' });
  };

  const openEdit = (client) => {
    setShowPassword(false);
    setForm({
      name: client.name, nickname: client.nickname || '', cpf: client.cpf || '',
      phone: client.phone || '', email: client.email || '',
      birth_date: isoToDate(client.birth_date),
      notes: client.notes || '',
      password: '', allow_multi_login: !!client.allow_multi_login, is_restricted: !!client.is_restricted,
      restriction_reason: client.restriction_reason || '', membership_plan_id:client.membership_plan_id||'',
      balance: Number(client.balance || 0).toFixed(2),
      time_balances: valueCategories.map(category => {
        const current=(client.time_balances||[]).find(item=>Number(item.category_id)===Number(category.id));
        return {
          category_id: category.id,
          category_name: category.name,
          remaining_minutes: String(Math.max(0, Number(current?.remaining_minutes || 0))),
        };
      }),
      financial_adjustment_method: 'cash',
      financial_adjustment_notes: ''
    });
    setModal({ type: 'form', client });
  };

  const openCredit = (client) => { setForm({ amount: '', payment_method: 'cash' }); setModal({ type: 'credit', client }); };
  const openPass = (client) => { setShowPassword(false); setForm({ password: '' }); setModal({ type: 'password', client }); };

  const handleAddCredit = async () => {
    if (!(parseFloat(form.amount) > 0)) { alert('Informe um valor'); return; }
    setLoading(true);
    try {
      await clientService.addBalance(modal.client.id, { amount: parseFloat(form.amount), payment_method: form.payment_method });
      setModal(null); load(search);
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  };

  const handleSetPass = async () => {
    if (!form.password || form.password.length < 4) { alert('Senha de ao menos 4 caracteres'); return; }
    setLoading(true);
    try {
      await clientService.setPassword(modal.client.id, { password: form.password });
      alert('Senha do cliente definida! Ele já pode logar na máquina.');
      setModal(null);
    } catch (err) { alert('Erro: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  };

  const cpfInvalid = !!form.cpf && !isValidCPF(form.cpf);
  const emailInvalid = !!form.email && !isValidEmail(form.email);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Clientes</h1>
          <p style={styles.sub}>{pagination.total || clients.length} clientes cadastrados</p>
        </div>
        <button style={styles.btnPrimary} onClick={openNew}>+ Novo Cliente</button>
      </div>

      <input
        style={styles.search}
        placeholder="🔍 Buscar por nome, CPF ou telefone..."
        value={search}
        onChange={handleSearch}
      />

      <div className="touch-scroll-x" style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {['Nome', 'CPF', 'Telefone', 'Saldo', 'Tempo restante', 'Total Gasto', 'Sessões', 'Ações'].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {clients.map(c => (
              <tr key={c.id} style={styles.tr}>
                <td style={styles.td}>
                  <div style={styles.clientName}>{c.name}</div>
                  {!!c.allow_multi_login && <div style={styles.supportBadge}>SUPORTE · LOGIN SIMULTÂNEO</div>}
                  {!!c.is_restricted && <div style={styles.restrictedBadge}>USUÁRIO RESTRITO</div>}
                  {c.email && <div style={styles.clientEmail}>{c.email}</div>}
                  {c.membership_plan_name&&<div style={styles.supportBadge}>{c.membership_plan_name} · {c.membership_status}</div>}
                </td>
                <td style={styles.td}>{c.cpf || '—'}</td>
                <td style={styles.td}>{c.phone || '—'}</td>
                <td style={{ ...styles.td, color: '#34d399' }}>R$ {parseFloat(c.balance || 0).toFixed(2)}</td>
                <td style={{ ...styles.td, color: 'var(--accent)', fontWeight: 800, whiteSpace: 'nowrap' }}>
                  {c.allow_multi_login ? '⏱ Livre' : (c.time_balances||[]).filter(item=>Number(item.remaining_minutes)>0).length
                    ? (c.time_balances||[]).filter(item=>Number(item.remaining_minutes)>0).map(item=>
                      <div key={item.category_id}>⏱ {item.category_name}: {formatMinutes(item.remaining_minutes)}</div>)
                    : '⏱ Sem tempo'}
                </td>
                <td style={styles.td}>R$ {parseFloat(c.total_spent || 0).toFixed(2)}</td>
                <td style={styles.td}>{c.total_sessions || 0}</td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                  <button style={styles.btnCredit} onClick={() => openCredit(c)}>💰 Crédito</button>
                  <button style={styles.btnSmall} onClick={() => openPass(c)}>🔑 Senha</button>
                  <button style={styles.btnSmall} onClick={() => openEdit(c)}>Editar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {clients.length === 0 && (
          <div style={styles.empty}>Nenhum cliente encontrado</div>
        )}
      </div>
      <div style={styles.pager}>
        <button style={styles.btnSecondary} disabled={pagination.page <= 1} onClick={() => load(search, pagination.page - 1)}>‹ Anterior</button>
        <span style={styles.pageInfo}>Página {pagination.page} de {pagination.pages} · {pagination.total} clientes</span>
        <button style={styles.btnSecondary} disabled={pagination.page >= pagination.pages} onClick={() => load(search, pagination.page + 1)}>Próxima ›</button>
      </div>

      {modal?.type === 'form' && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, ...(modal.type === 'form' ? styles.modalWide : {}) }}>
            <h2 style={styles.modalTitle}>{modal.client ? 'Editar Cliente' : 'Novo Cliente'}</h2>
            {modal.client && (
              <div style={styles.adjustBox}>
                <div style={styles.balanceGrid}>
                  <div style={styles.fieldCompact}>
                    <label style={styles.label}>Saldo em R$</label>
                    <input type="number" min="0" step="0.01" style={styles.input}
                      value={form.balance} onChange={e => setForm({ ...form, balance: e.target.value })} />
                    <small style={styles.optHint}>Atual: R$ {parseFloat(modal.client.balance || 0).toFixed(2)}</small>
                  </div>
                  <div style={styles.fieldCompact}>
                    <label style={styles.label}>Tempo total (minutos)</label>
                    <input type="number" min="0" step="1" style={{...styles.input, ...styles.timeTotal, padding: '4px 14px', background: '#1a1a24'}}
                      value={Math.round((form.time_balances||[]).reduce((sum,item)=>sum+Number(item.remaining_minutes||0),0))}
                      onChange={e => {
                        const newTotal = Number(e.target.value);
                        setForm(current => {
                          const newBalances = [...(current.time_balances || [])];
                          if (newBalances.length > 0) {
                             const otherSum = newBalances.slice(1).reduce((sum, item) => sum + Number(item.remaining_minutes||0), 0);
                             newBalances[0] = { ...newBalances[0], remaining_minutes: Math.max(0, newTotal - otherSum) };
                          }
                          return { ...current, time_balances: newBalances };
                        });
                      }}
                    />
                    <small style={styles.optHint}>Soma total. Digite para ajustar a 1ª categoria.</small>
                  </div>
                </div>
                <label style={styles.label}>Saldo por categoria de valores (minutos)</label>
                <div style={styles.balanceGrid}>
                  {(form.time_balances||[]).map((item,index)=><div key={item.category_id} style={styles.fieldCompact}>
                    <label style={styles.categoryLabel}>{item.category_name}</label>
                    <input type="number" min="0" step="1" style={styles.input}
                      value={Math.round(item.remaining_minutes||0)}
                      onChange={e=>setForm(current=>({
                        ...current,
                        time_balances:current.time_balances.map((row,rowIndex)=>rowIndex===index
                          ? {...row,remaining_minutes:e.target.value}
                          : row)
                      }))}/>
                    <small style={styles.optHint}>{formatMinutes(item.remaining_minutes)}</small>
                  </div>)}
                </div>
                <div style={styles.balanceGrid}>
                  <div style={styles.fieldCompact}>
                    <label style={styles.label}>Forma do ajuste financeiro</label>
                    <select style={styles.input} value={form.financial_adjustment_method}
                      onChange={e => setForm({ ...form, financial_adjustment_method: e.target.value })}>
                      <option value="cash">Dinheiro</option>
                      <option value="pix">PIX</option>
                      <option value="card_debit">Cartão débito</option>
                      <option value="card_credit">Cartão crédito</option>
                      <option value="courtesy">Cortesia / Isento</option>
                      <option value="mercadopago">Mercado Pago</option>
                    </select>
                  </div>
                  <div style={styles.fieldCompact}>
                    <label style={styles.label}>Motivo do ajuste</label>
                    <input style={styles.input} placeholder="Ex.: correção de lançamento"
                      value={form.financial_adjustment_notes}
                      onChange={e => setForm({ ...form, financial_adjustment_notes: e.target.value })} />
                  </div>
                </div>
                <small style={styles.adjustHint}>Alteração em R$ gera lançamento no Financeiro/Caixa. O tempo é separado por categoria de valores e não é transferido entre aparelhos diferentes.</small>
              </div>
            )}

            <div style={styles.formGrid}>
            <div style={{ ...styles.field, ...styles.fullSpan }}>
              <label style={styles.label}>Nome *</label>
              <input style={styles.input} placeholder="Nome completo"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>

            <div style={{ ...styles.loginHint, ...styles.fullSpan }}>🔑 O cliente loga na máquina com o <b>nick</b> ou o <b>WhatsApp</b> + senha.</div>

            <div style={styles.field}>
              <label style={styles.label}>Data de Aniversário *</label>
              <input
                type="text"
                placeholder="DD/MM/AAAA"
                inputMode="numeric"
                maxLength={10}
                style={{ ...styles.input, ...(!form.birth_date || (form.birth_date.replace(/\D/g, '').length === 8 && !isValidDate(form.birth_date)) ? styles.inputError : {}) }}
                value={form.birth_date || ''}
                onChange={e => setForm({ ...form, birth_date: maskDate(e.target.value) })}
              />
              {!!form.birth_date && form.birth_date.replace(/\D/g, '').length === 8 && !isValidDate(form.birth_date) && (
                <div style={styles.errText}>Data de aniversário inválida (DD/MM/AAAA)</div>
              )}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Nickname (apelido de login)</label>
              <input style={styles.input} placeholder="ex: shadowgamer"
                value={form.nickname} onChange={e => setForm({ ...form, nickname: e.target.value })} />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>WhatsApp (login principal)</label>
              <input style={styles.input} placeholder="(00) 00000-0000" inputMode="numeric" maxLength={15}
                value={form.phone} onChange={e => setForm({ ...form, phone: maskPhone(e.target.value) })} />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>CPF <span style={styles.optHint}>(opcional)</span></label>
              <input style={{ ...styles.input, ...(cpfInvalid ? styles.inputError : {}) }}
                placeholder="000.000.000-00" inputMode="numeric" maxLength={14}
                value={form.cpf} onChange={e => setForm({ ...form, cpf: maskCPF(e.target.value) })} />
              {cpfInvalid && <div style={styles.errText}>CPF inválido</div>}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input style={{ ...styles.input, ...(emailInvalid ? styles.inputError : {}) }}
                placeholder="email@exemplo.com" inputMode="email" type="email"
                value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              {emailInvalid && <div style={styles.errText}>Email inválido</div>}
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Senha de acesso {modal.client
                ? <span style={styles.optHint}>(deixe em branco para manter)</span>
                : <span style={styles.optHint}>(opcional; pode definir depois)</span>}</label>
              <div style={styles.passwordWrap}>
                <input type={showPassword ? 'text' : 'password'} style={{ ...styles.input, paddingRight: 46 }} placeholder="mín. 4 caracteres"
                  value={form.password || ''} onChange={e => setForm({ ...form, password: e.target.value })} />
                <button type="button" style={styles.eyeBtn} onClick={() => setShowPassword(!showPassword)}>{showPassword ? '🙈' : '👁️'}</button>
              </div>
            </div>

            <label style={styles.specialLogin}>
              <input type="checkbox" checked={!!form.allow_multi_login}
                onChange={e => setForm({ ...form, allow_multi_login: e.target.checked })} />
              <span><b>Acesso livre / suporte</b><br /><small>Login simultâneo e jogos ilimitados. Sem PIX, pacotes ou pedidos.</small></span>
            </label>
            <label style={{...styles.specialLogin,...styles.restrictedLogin}}>
              <input type="checkbox" checked={!!form.is_restricted}
                onChange={e => setForm({ ...form, is_restricted: e.target.checked, allow_multi_login: e.target.checked ? false : form.allow_multi_login })} />
              <span><b>Usuário restrito</b><br /><small>Impede acesso ao `.exe`, portal do jogador e novos agendamentos.</small></span>
            </label>
            {form.is_restricted && <div style={{ ...styles.field, ...styles.fullSpan }}>
              <label style={styles.label}>Motivo da restrição <span style={styles.optHint}>(somente equipe)</span></label>
              <textarea style={{...styles.input,height:70,borderColor:'#7f1d1d'}} placeholder="Ex.: Responsável solicitou bloqueio de acesso"
                value={form.restriction_reason||''} onChange={e=>setForm({...form,restriction_reason:e.target.value})}/>
            </div>}
            <div style={styles.field}><label style={styles.label}>Plano de assinatura</label><select style={styles.input} value={form.membership_plan_id||''} onChange={e=>setForm({...form,membership_plan_id:e.target.value})}><option value="">Sem plano atribuído</option>{plans.map(p=><option key={p.id} value={p.id}>{p.name} · R$ {Number(p.price).toFixed(2)}</option>)}</select><small style={styles.optHint}>A franquia é liberada somente após a confirmação do pagamento.</small></div>

            <div style={{ ...styles.field, ...styles.fullSpan }}>
              <label style={styles.label}>Observações</label>
              <textarea style={{ ...styles.input, height: 70 }} value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setModal(null)}>Cancelar</button>
              <button style={styles.btnPrimary} onClick={handleSave}
                disabled={loading || !form.name || !form.birth_date || form.birth_date.replace(/\D/g, '').length !== 8 || !isValidDate(form.birth_date) || cpfInvalid || emailInvalid}>
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADICIONAR CRÉDITO */}
      {modal?.type === 'credit' && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Adicionar crédito — {modal.client.name}</h2>
            <div style={styles.balInfo}>Saldo atual: R$ {parseFloat(modal.client.balance || 0).toFixed(2)}</div>
            <div style={styles.field}>
              <label style={styles.label}>Valor (R$)</label>
              <input type="number" step="0.01" style={styles.input} autoFocus
                value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Forma de pagamento</label>
              <select style={styles.input} value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                <option value="cash">Dinheiro</option>
                <option value="pix">PIX</option>
                <option value="card_debit">Cartão débito</option>
                <option value="card_credit">Cartão crédito</option>
                <option value="courtesy">Cortesia / Isento</option>
              </select>
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setModal(null)}>Cancelar</button>
              <button style={styles.btnPrimary} onClick={handleAddCredit} disabled={loading}>{loading ? '...' : 'Adicionar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* DEFINIR SENHA */}
      {modal?.type === 'password' && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>Senha de login — {modal.client.name}</h2>
            <div style={styles.balInfo}>O cliente usa o e-mail/CPF + esta senha para logar na máquina.</div>
            <div style={styles.field}>
              <label style={styles.label}>Nova senha</label>
              <div style={styles.passwordWrap}>
                <input type={showPassword ? 'text' : 'password'} style={{ ...styles.input, paddingRight: 46 }} autoFocus placeholder="mín. 4 caracteres"
                  value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                <button type="button" style={styles.eyeBtn} onClick={() => setShowPassword(!showPassword)}>{showPassword ? '🙈' : '👁️'}</button>
              </div>
            </div>
            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setModal(null)}>Cancelar</button>
              <button style={styles.btnPrimary} onClick={handleSetPass} disabled={loading}>{loading ? '...' : 'Definir senha'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: '100%', paddingBottom: 28 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 },
  title: { color: '#fff', fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { color: '#6b7280', fontSize: 14, margin: '4px 0 0' },
  search: { width: '100%', padding: '10px 16px', background: '#14141f', border: '1px solid #262636', borderRadius: 8, color: '#fff', fontSize: 14, marginBottom: 20, boxSizing: 'border-box' },
  tableWrap: { background: '#14141f', border: '1px solid #262636', borderRadius: 12 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#6b7280', fontSize: 12, fontWeight: 600, textAlign: 'left', padding: '12px 16px', borderBottom: '1px solid #262636', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #262636' },
  td: { color: '#e5e7eb', fontSize: 14, padding: '12px 16px' },
  clientName: { fontWeight: 600 },
  clientEmail: { color: '#6b7280', fontSize: 12, marginTop: 2 },
  supportBadge: { display: 'inline-block', color: '#bef264', background: '#1a2e05', border: '1px solid #3f6212', borderRadius: 5, padding: '2px 6px', fontSize: 10, fontWeight: 800, marginTop: 4 },
  restrictedBadge: { display: 'inline-block', color: '#fecaca', background: '#450a0a', border: '1px solid #991b1b', borderRadius: 5, padding: '2px 6px', fontSize: 10, fontWeight: 900, marginTop: 4, marginLeft: 5 },
  empty: { color: '#6b7280', textAlign: 'center', padding: 40 },
  pager: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' },
  pageInfo: { color: '#8b8b9e', fontSize: 12, fontWeight: 700 },
  btnPrimary: { padding: '10px 20px', background: '#1d4ed8', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnSecondary: { padding: '8px 16px', background: 'transparent', border: '1px solid #374151', borderRadius: 6, color: '#9ca3af', cursor: 'pointer', fontSize: 13 },
  btnSmall: { padding: '5px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 6, color: '#9ca3af', cursor: 'pointer', fontSize: 12, marginLeft: 6 },
  btnCredit: { padding: '5px 12px', background: '#064e3b', border: '1px solid #065f46', borderRadius: 6, color: '#34d399', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  balInfo: { color: '#9ca3af', fontSize: 13, marginBottom: 16, background: '#0a0a0f', padding: '8px 12px', borderRadius: 8 },
  balanceGrid: { display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 10, marginBottom: 18 },
  adjustBox: { background: '#0a0a0f', border: '1px solid #262636', borderRadius: 12, padding: 12, marginBottom: 18 },
  fieldCompact: { marginBottom: 0, minWidth: 0 },
  categoryLabel: { display: 'block', color: '#bef264', fontSize: 12, marginBottom: 6, fontWeight: 800 },
  timeTotal: { color: '#a3e635', fontSize: 20, fontWeight: 900, minHeight: 42, display: 'flex', alignItems: 'center' },
  adjustHint: { display: 'block', color: '#a3e635', fontSize: 11, marginTop: -6 },
  balanceCard: { minWidth: 0, background: '#0a0a0f', border: '1px solid #262636', borderRadius: 10, padding: '11px 12px', display: 'grid', gap: 4 },
  balanceLabel: { color: '#8b8b9e', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .6 },
  balanceValue: { color: '#34d399', fontSize: 18, lineHeight: 1.1 },
  overlay: { position: 'fixed', inset: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '24px 12px' },
  modal: { background: '#14141f', border: '1px solid #262636', borderRadius: 16, padding: 32, width: 440, maxWidth: '90vw', maxHeight: 'calc(100dvh - 48px)', overflowY: 'auto', boxSizing: 'border-box' },
  modalWide: { width: 760, maxWidth: 'calc(100vw - 28px)', overflowX: 'hidden' },
  modalTitle: { color: '#fff', fontSize: 20, fontWeight: 700, marginTop: 0, marginBottom: 24 },
  modalActions: { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '0 14px' },
  fullSpan: { gridColumn: '1 / -1' },
  field: { marginBottom: 16 },
  label: { display: 'block', color: '#9ca3af', fontSize: 13, marginBottom: 6, fontWeight: 500 },
  specialLogin: { display: 'flex', gap: 10, alignItems: 'flex-start', color: '#e5e7eb', background: '#1a2e05', border: '1px solid #3f6212', borderRadius: 9, padding: 12, marginBottom: 16, cursor: 'pointer', fontSize: 13 },
  restrictedLogin: { background: '#2b0b0b', borderColor: '#991b1b' },
  input: { width: '100%', padding: '10px 14px', background: '#262636', border: '1px solid #374151', borderRadius: 8, color: '#fff', fontSize: 14, boxSizing: 'border-box' },
  passwordWrap: { position: 'relative' },
  eyeBtn: { position: 'absolute', right: 6, top: 6, width: 34, height: 30, border: 0, borderRadius: 7, background: '#1a1a28', color: '#fff', cursor: 'pointer' },
  inputError: { border: '1px solid #ef4444' },
  errText: { color: '#f87171', fontSize: 12, marginTop: 4 },
  loginHint: { color: '#93c5fd', fontSize: 12, background: '#0a0a0f', border: '1px solid #1e3a5f', borderRadius: 8, padding: '8px 12px', marginBottom: 14 },
  optHint: { color: '#6b7280', fontSize: 11, fontWeight: 400 },
};
